'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { MarketKey, UIMarket } from '@drift/common';

// TODO: we should either store data by marketIdKey or marketSymbol, not both
export interface MarketSettingsState {
	/** key: marketSymbol, value: position max leverage */
	marketLeverages: Array<{ marketSymbol: string; positionMaxLeverage: number }>;
	/** key: marketIdKey, value: margin mode ('cross' | 'isolated') */
	marketMarginModes: Array<{
		marketIdKey: MarketKey;
		marginMode: 'cross' | 'isolated';
		userKey: string;
	}>;
	setMaxLeverageForMarket: (
		marketSymbol: string,
		value: number | undefined
	) => void;
	getMaxLeverageForMarket: (
		marketSymbol: string | undefined
	) => number | undefined;
	getMaxLeverageForMarketIndex: (marketIndex: number) => number | undefined;
	setMarginModeForMarket: (
		marketIdKey: MarketKey,
		value: 'cross' | 'isolated' | undefined,
		userKey: string
	) => void;
	getMarginModeForMarket: (
		marketIdKey: MarketKey | undefined,
		userKey: string
	) => 'cross' | 'isolated' | undefined;
	syncMarketModesFromOpenPositions: (
		locks: Array<{
			marketIdKey: MarketKey;
			marginMode: 'cross' | 'isolated';
			userKey: string;
		}>
	) => void;
}

/**
 * Stores the user's max leverage for markets that are in their `PerpPosition` data.
 */
const useMarketSettingsStore = create<MarketSettingsState>()(
	persist(
		(set, get) => {
			const getMaxLeverageForMarket = (
				marketSymbol: string | undefined
			): number | undefined => {
				return get().marketLeverages.find(
					(m) => m.marketSymbol === marketSymbol
				)?.positionMaxLeverage;
			};

			return {
				marketLeverages: [],
				setMaxLeverageForMarket: (
					marketSymbol: string,
					value: number | undefined
				) => {
					set((s) => {
						const next = [...s.marketLeverages];
						if (value === undefined) {
							const idx = next.findIndex(
								(m) => m.marketSymbol === marketSymbol
							);
							if (idx >= 0) {
								next.splice(idx, 1);
							}
						} else {
							const idx = next.findIndex(
								(m) => m.marketSymbol === marketSymbol
							);
							if (idx >= 0) {
								next[idx] = { ...next[idx], positionMaxLeverage: value };
							} else {
								next.push({ marketSymbol, positionMaxLeverage: value });
							}
						}
						s.marketLeverages = next;
						return s;
					});
				},
				getMaxLeverageForMarket,
				getMaxLeverageForMarketIndex: (
					marketIndex: number
				): number | undefined => {
					const marketSymbol = UIMarket.createPerpMarket(marketIndex).symbol;
					return getMaxLeverageForMarket(marketSymbol);
				},
				marketMarginModes: [],
				setMarginModeForMarket: (
					marketIdKey: MarketKey,
					value: 'cross' | 'isolated' | undefined,
					userKey: string
				) => {
					set((s) => {
						const next = [...s.marketMarginModes];
						if (value === undefined) {
							const idx = next.findIndex(
								(m) => m.marketIdKey === marketIdKey && m.userKey === userKey
							);
							if (idx >= 0) {
								next.splice(idx, 1);
							}
						} else {
							const idx = next.findIndex(
								(m) => m.marketIdKey === marketIdKey && m.userKey === userKey
							);
							if (idx >= 0) {
								next[idx] = { ...next[idx], marginMode: value, userKey };
							} else {
								next.push({ marketIdKey, marginMode: value, userKey });
							}
						}
						s.marketMarginModes = next;
						return s;
					});
				},
				getMarginModeForMarket: (
					marketIdKey: MarketKey | undefined,
					userKey: string
				): 'cross' | 'isolated' | undefined => {
					return get().marketMarginModes.find(
						(m) => m.marketIdKey === marketIdKey && m.userKey === userKey
					)?.marginMode;
				},
				syncMarketModesFromOpenPositions: (
					modes: Array<{
						marketIdKey: MarketKey;
						marginMode: 'cross' | 'isolated';
						userKey: string;
					}>
				) => {
					set((s) => {
						const next = [...s.marketMarginModes];
						for (const mode of modes) {
							const idx = next.findIndex(
								(m) =>
									m.marketIdKey === mode.marketIdKey &&
									m.userKey === mode.userKey
							);
							if (idx >= 0) {
								next[idx] = {
									...next[idx],
									marginMode: mode.marginMode,
									userKey: mode.userKey,
								};
							} else {
								next.push({
									marketIdKey: mode.marketIdKey,
									marginMode: mode.marginMode,
									userKey: mode.userKey,
								});
							}
						}
						s.marketMarginModes = next;
						return s;
					});
				},
			};
		},
		{
			name: 'marketSettings',
			storage: createJSONStorage(() => localStorage),
		}
	)
);

export default useMarketSettingsStore;
